import { useEffect, useMemo, useState } from "react";
import type { configurationTypes } from "@repo/models";
import { Input } from "@repo/ui";
import { WorkflowsInputControl } from "../workflows-input-control";

type CreateStatusRequest = configurationTypes.components["schemas"]["CreateStatusRequest"];

type Props = {
  status: CreateStatusRequest | null;
  onSave: (updatedStatus: CreateStatusRequest) => void;
  takenCodes: number[];
  onCancel?: () => void;
  onHasUnsavedChanges?: (hasChanges: boolean, onDiscard: () => void) => void;
};

export const StatusInput = ({
  status,
  onSave,
  takenCodes,
  onCancel,
  onHasUnsavedChanges,
}: Props) => {
  const [localCopy, setLocalCopy] = useState<CreateStatusRequest | null>(status);
  const [originalStatus, setOriginalStatus] = useState<CreateStatusRequest | null>(status);

  const hasUnsavedChanges = useMemo(() => {
    if (!originalStatus || !localCopy) return false;
    return JSON.stringify(originalStatus) !== JSON.stringify(localCopy);
  }, [originalStatus, localCopy]);

  const isDuplicate = useMemo(() => {
    if (!localCopy) return false;
    return takenCodes.includes(localCopy.code);
  }, [localCopy, takenCodes]);

  const handleCancelInternal = () => {
    setLocalCopy(originalStatus);
    onCancel?.();
  };

  useEffect(() => {
    onHasUnsavedChanges?.(hasUnsavedChanges, handleCancelInternal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges]);

  const handleSaveInternal = () => {
    if (!localCopy) return;
    if (isDuplicate) return;
    onSave(localCopy);
    setOriginalStatus(localCopy);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.defaultPrevented) return;

    switch (e.key) {
      case "Escape":
        e.stopPropagation();
        handleCancelInternal();
        break;

      case "Enter":
        if (isDuplicate) return;
        if ((e.target as HTMLElement).tagName === "TEXTAREA") return;
        e.stopPropagation();
        e.preventDefault();
        handleSaveInternal();
        break;
    }
  };

  if (!status || !localCopy)
    return <div className="p-4 text-gray-400">Select a status to edit</div>;

  return (
    <div className="relative flex flex-col gap-2 border-b border-b-gray-300 pb-12">
      <div className="flex gap-4" onKeyDown={handleKeyDown}>
        <Input
          error={isDuplicate ? "Duplicate code" : undefined}
          containerClassName="w-20"
          label="No."
          type="number"
          value={localCopy.code}
          onChange={(e) =>
            setLocalCopy({
              ...localCopy,
              code: Number(e.target.value),
            })
          }
        />
        <Input
          label="Status Name"
          className="w-full flex-[1]"
          value={localCopy.name}
          onChange={(e) =>
            setLocalCopy({
              ...localCopy,
              name: e.target.value,
            })
          }
        />
      </div>
      {hasUnsavedChanges && (
        <div className="absolute right-0 bottom-3 ml-auto flex gap-2">
          <WorkflowsInputControl controlType="cancel" onClick={handleCancelInternal} />
          <WorkflowsInputControl
            controlType="confirm"
            onClick={handleSaveInternal}
            disabled={isDuplicate}
          />
        </div>
      )}
    </div>
  );
};
